import { useCallback, useRef } from 'react';
import { useTerminalAccount } from '@src/context/AccountContext';
import { useNetworkStatus } from '@src/context/NetworkStatusContext';
import {
    handleOfflinePunch as dispatchOfflinePunch,
    completeOfflinePunch as resumeOfflinePunch
} from '@src/config/offlinePunchService';
import type {
    OfflinePunchResult,
    OfflinePunchSelection
} from '@src/config/offlinePunchService';

/**
 * React-facing offline punch service — wires the current account (company,
 * location) and connectivity state (`offlineSince`) into the pure dispatch
 * logic in `offlinePunchService.ts`, per API-4650.
 */
export interface OfflinePunchService {
    handleOfflinePunch: (pin: string) => Promise<OfflinePunchResult>;
    completeOfflinePunch: (
        pin: string,
        selection: OfflinePunchSelection
    ) => Promise<OfflinePunchResult>;
}

export function useOfflinePunch(): OfflinePunchService {
    const { account } = useTerminalAccount();
    const { offlineSince } = useNetworkStatus();

    // Refs so the callbacks stay stable while always reading current values —
    // mirrors the tokenProvider pattern in useTimePunchClockApi.
    const accountRef = useRef(account);
    accountRef.current = account;
    const offlineSinceRef = useRef(offlineSince);
    offlineSinceRef.current = offlineSince;

    const handleOfflinePunch = useCallback(
        async (pin: string): Promise<OfflinePunchResult> => {
            const current = accountRef.current;
            if (!current) {
                return { type: 'error', reason: 'no_account' };
            }
            return dispatchOfflinePunch(pin, {
                companyId: current.companyId,
                locationId: current.locationId,
                locationHash: current.locationHash,
                offlineSince: offlineSinceRef.current
            });
        },
        []
    );

    const completeOfflinePunch = useCallback(
        async (
            pin: string,
            selection: OfflinePunchSelection
        ): Promise<OfflinePunchResult> => {
            const current = accountRef.current;
            if (!current) {
                return { type: 'error', reason: 'no_account' };
            }
            return resumeOfflinePunch(
                pin,
                {
                    companyId: current.companyId,
                    locationHash: current.locationHash
                },
                selection
            );
        },
        []
    );

    return { handleOfflinePunch, completeOfflinePunch };
}
